import React, { useCallback, useMemo, useState } from "react";
import { useFeatureFlag } from "@jsq/flags-react";
import { OtherPaymentPrefInput } from "#components/PaymentPrefConsolidated/Components/OtherPaymentPrefInput";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { AchPaymentPrefInput } from "../../Components/AchPaymentPrefInput";
import { BacsPaymentPrefInput } from "../../Components/BacsPaymentPrefInput";
import { BecsPaymentPrefInput } from "../../Components/BecsPaymentPrefInput";
import { CheckPaymentPrefInput } from "../../Components/CheckPaymentPrefInput";
import { EftPaymentPrefInput } from "../../Components/EftPaymentPrefInput";
import { SelectPaymentMethod } from "../../Components/SelectPaymentMethod";
import { SelectPaymentMethodV2 } from "../../Components/SelectPaymentMethodV2";
import { SepaPaymentPrefInput } from "../../Components/SepaPaymentPrefInput";
import { type ChildComponentProps } from "../../Components/types";
import { WirePaymentPrefInput } from "../../Components/WirePaymentPrefInput";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ConsolidatedPaymentPrefContextType } from "../../Context/types";
import { type BasePaymentPrefWorkflowProps } from "./types";

const getPaymentPrefForm = (paymentMethod?: PaymentMethod) => {
  switch (paymentMethod) {
    case PaymentMethod.ACH:
      return AchPaymentPrefInput;
    case PaymentMethod.BACS:
      return BacsPaymentPrefInput;
    case PaymentMethod.BECS:
      return BecsPaymentPrefInput;
    case PaymentMethod.Check:
      return CheckPaymentPrefInput;
    case PaymentMethod.SEPA:
      return SepaPaymentPrefInput;
    case PaymentMethod.EFT:
      return EftPaymentPrefInput;
    case PaymentMethod.Wire:
      return WirePaymentPrefInput;
    case PaymentMethod.Other:
      return OtherPaymentPrefInput;
    default:
      throw new Error("unsupported payment method");
  }
};

type GetWorkflowComponentsParams = Pick<
  ConsolidatedPaymentPrefContextType,
  "selectedPaymentMethod" | "isExistingPaymentPref" | "singleAvailablePaymentMethod"
> &
  Pick<
    BasePaymentPrefWorkflowProps,
    "prefixComponents" | "suffixComponents" | "workflowMode" | "allowPaymentMethodChange"
  > & {
    useInternationalPaymentRails?: boolean;
  };

const getWorkflowComponents = ({
  selectedPaymentMethod,
  isExistingPaymentPref,
  singleAvailablePaymentMethod,
  workflowMode,
  prefixComponents,
  suffixComponents,
  allowPaymentMethodChange = false,
  useInternationalPaymentRails = false,
}: GetWorkflowComponentsParams) => {
  const workflowComponents: React.FC<ChildComponentProps>[] = [];

  if (prefixComponents) {
    workflowComponents.push(...prefixComponents);
  }

  // Show payment method selection and forms in Create mode OR in Edit mode when allowPaymentMethodChange is true
  const shouldShowPaymentFlow = !isExistingPaymentPref || allowPaymentMethodChange;

  if (shouldShowPaymentFlow) {
    // Show payment method selection in Create mode (when multiple methods available)
    // In Edit mode with allowPaymentMethodChange:
    // - Show selection if selectedPaymentMethod is NOT set (user clicked "Switch payment method")
    // - Skip selection if selectedPaymentMethod IS set (user clicked "Edit instructions")
    const shouldShowPaymentMethodSelection =
      !singleAvailablePaymentMethod &&
      (workflowMode === WorkflowMode.Create ||
        (allowPaymentMethodChange && !selectedPaymentMethod));

    if (shouldShowPaymentMethodSelection) {
      workflowComponents.push(
        useInternationalPaymentRails ? SelectPaymentMethodV2 : SelectPaymentMethod,
      );
    }

    if (selectedPaymentMethod) {
      const paymentFormInput = getPaymentPrefForm(selectedPaymentMethod);
      workflowComponents.push(paymentFormInput);
    }
  }

  if (suffixComponents) {
    workflowComponents.push(...suffixComponents);
  }

  return workflowComponents;
};

/**
 * A base workflow component that provides the core functionality for payment preference workflows.
 * It manages step navigation, form state, and component rendering for both creation and editing
 * of payment preferences.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {WorkflowMode} props.workflowMode - Mode of the workflow (Create or Edit)
 * @param {React.FC<ChildComponentProps>[]} [props.prefixComponents] - Components to render before the main form
 * @param {React.FC<ChildComponentProps>[]} [props.suffixComponents] - Components to render after the main form
 *
 * @example
 * ```tsx
 * <BasePaymentPrefWorkflow
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.Create}
 *   prefixComponents={[SelectAvailablePaymentPrefsForm]}
 *   suffixComponents={[PaymentPrefConfirmation]}
 * />
 * ```
 *
 * @remarks
 * - Uses useConsolidatedPaymentPrefContext for state management
 * - Manages step-by-step workflow navigation
 * - Supports dynamic component injection through prefix and suffix components
 * - Handles both create and edit modes
 * - Provides consistent back/continue navigation
 * - Supports customizable back button naming
 *
 * @see {@link PaymentPrefCreationForPositionWorkflow} for creation workflow implementation
 * @see {@link PaymentPrefEditForAccountAndPositionWorkflow} for edit workflow implementation
 */
export const BasePaymentPrefWorkflow: React.FC<BasePaymentPrefWorkflowProps> = ({
  onExit,
  onSuccess,
  workflowMode,
  prefixComponents,
  suffixComponents,
  onBeforeContinue,
  allowPaymentMethodChange,
}) => {
  const { selectedPaymentMethod, isExistingPaymentPref, singleAvailablePaymentMethod } =
    useConsolidatedPaymentPrefContext();
  const useInternationalPaymentRails = useFeatureFlag("internationalPaymentRails", false);
  const [step, setStep] = useState(0);

  const workflowComponents = useMemo(
    () =>
      getWorkflowComponents({
        selectedPaymentMethod,
        isExistingPaymentPref,
        singleAvailablePaymentMethod,
        workflowMode,
        prefixComponents,
        suffixComponents,
        allowPaymentMethodChange,
        useInternationalPaymentRails,
      }),
    [
      isExistingPaymentPref,
      singleAvailablePaymentMethod,
      prefixComponents,
      selectedPaymentMethod,
      suffixComponents,
      workflowMode,
      allowPaymentMethodChange,
      useInternationalPaymentRails,
    ],
  );

  // Navigate to the next step, or call onSuccess if we're at the last step.
  // Child components are responsible for calling this at the appropriate time:
  // - Most components call this after form submission
  // - SelectPaymentMethod in Edit mode does NOT call this because selecting a
  //   payment method causes the component at the current index to change
  //   (SelectPaymentMethod is replaced by the payment form at the same index)
  const handleContinuePress = useCallback(() => {
    if (step < workflowComponents.length - 1) {
      setStep(step + 1);
    } else {
      onSuccess();
    }
  }, [step, workflowComponents.length, onSuccess]);

  const handleBackPress = useCallback(() => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      onExit();
    }
  }, [onExit, step]);

  const CurrentStep = workflowComponents[step];

  return (
    <CurrentStep
      onContinueClick={handleContinuePress}
      onBackClick={handleBackPress}
      onBackButtonName={step === 0 ? "Cancel" : undefined}
      onBeforeContinue={onBeforeContinue}
    />
  );
};
